import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useLeadModal } from "../context/LeadModalContext";
import { categoryBySlug, ICON_MAP } from "../data/categories";
import { ChevronMark } from "../components/ChevronMark";
import { Reveal } from "../components/Reveal";

const LEGAL = {
  "privacy-policy": {
    name: "Privacy Policy",
    group: "Legal",
    tagline: "How we collect, use, and protect your information.",
  },
  "terms-of-service": {
    name: "Terms of Service",
    group: "Legal",
    tagline: "The terms that govern the use of our platform and services.",
  },
};

export default function ComingSoonPage({ slug }) {
  const { openLeadForm } = useLeadModal();
  const cat =
    categoryBySlug(slug) ||
    LEGAL[slug] || { name: "Page Not Found", group: "", tagline: "The page you're looking for doesn't exist or has moved." };
  const Icon = cat.icon ? ICON_MAP[cat.icon] : null;

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Please enter a valid email address.");
    }
    setLoading(true);
    try {
      await api.post("/notify", { email, topic: cat.name });
      setSubscribed(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-bone pt-[72px]" data-testid={`coming-soon-${slug}`}>
      <div className="pointer-events-none absolute -right-24 top-24 opacity-[0.05]">
        <ChevronMark className="h-[420px] w-auto" />
      </div>

      <div className="relative mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:py-28">
        <Reveal>
          <nav className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground" data-testid="breadcrumb">
            <Link to="/" className="transition-colors hover:text-forest">Home</Link>
            <span>/</span>
            {cat.group && (
              <>
                <span>{cat.group}</span>
                <span>/</span>
              </>
            )}
            <span className="text-gold-dark">{cat.name}</span>
          </nav>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-12 flex items-center gap-5">
            {Icon && (
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl border border-gold/30 bg-gold/10 text-gold-dark">
                <Icon className="h-7 w-7" />
              </span>
            )}
            <span className="rounded-full border border-forest/20 bg-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-forest">
              Coming Soon
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <h1 className="mt-8 font-display text-4xl font-extrabold tracking-tight text-forest-deep sm:text-5xl">
            {cat.name}
          </h1>
        </Reveal>
        <Reveal delay={0.24}>
          <p className="mt-4 max-w-xl font-editorial text-xl italic leading-relaxed text-forest/70">
            {cat.tagline}
          </p>
        </Reveal>

        <Reveal delay={0.32}>
          <div className="mt-12 rounded-[2rem] border border-border bg-white p-8 shadow-[0_25px_70px_rgba(10,41,30,0.08)] sm:p-10" data-testid="coming-soon-block">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold-dark">In development</p>
            <h2 className="mt-3 font-display text-xl font-bold text-forest-deep sm:text-2xl">
              This hub is being crafted right now.
            </h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-muted-foreground sm:text-base">
              We're preparing the full {cat.name} experience. Leave your email and you'll be the first to
              know when it launches — or book a free strategy call and start today with a consultant.
            </p>

            {subscribed ? (
              <p className="mt-6 flex items-center gap-2.5 rounded-2xl bg-forest/5 px-5 py-4 text-sm font-semibold text-forest" data-testid="notify-success">
                <Check className="h-4 w-4 text-gold-dark" />
                You're on the list — we'll email you the moment it launches.
              </p>
            ) : (
              <form onSubmit={subscribe} className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="notify-form">
                <input
                  data-testid="notify-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12 flex-1 rounded-full border border-input bg-bone px-5 text-sm outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-muted-foreground/60 focus:border-gold focus:ring-2 focus:ring-gold/25"
                />
                <button type="submit" data-testid="notify-submit-btn" disabled={loading}
                  className="flex h-12 items-center justify-center gap-2 rounded-full bg-forest px-6 font-display text-sm font-bold text-bone transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
                  Notify me
                </button>
              </form>
            )}

            <div className="mt-8 flex flex-col gap-3 border-t border-border pt-7 sm:flex-row">
              <button
                data-testid="coming-soon-book-call-btn"
                onClick={() => openLeadForm(`category:${slug}`)}
                className="group flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display text-sm font-bold text-forest-deep transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(212,175,55,0.4)]"
              >
                Book a Free Strategy Call
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <Link
                to="/"
                data-testid="coming-soon-back-home"
                className="flex items-center justify-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold text-foreground/70 transition-colors hover:border-forest/40 hover:text-forest"
              >
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
